import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DocumentItem from "../../../Components/Masters/DocumentItem";
import { formatDate } from "../../../utils/formatDate";

function ViewData({
  title,
  inputData,
  element,
  viewData,
  setViewData,
  handleEdit,
  companyAdmin,
  setData,
}) {
  const [filteredData, setFilteredData] = useState({});

  useEffect(() => {
    if (element) setFilteredData(element);
  }, [element]);

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => acc?.[part], obj);
  };

  const formatFuelDisplay = (data) => {
    if (data?.fuelDisplay) return data.fuelDisplay;
    const entries = Array.isArray(data?.fuelEntries) ? data.fuelEntries : [];
    if (entries.length > 0) {
      return entries
        .map((f) => {
          const name = f?.fuelType || f?.fuelName || "";
          const qty = f?.fuelConsumption ?? f?.quantity ?? "";
          const unit = f?.fuelConsumptionUnit || f?.unit || "";
          return qty ? `${name} ${qty} ${unit}`.trim() : name;
        })
        .filter(Boolean)
        .join(", ");
    }
    if (data?.fuelType) {
      return `${data.fuelType} ${data.fuelConsumption || ""} ${data.fuelConsumptionUnit || ""}`.trim();
    }
    return "-";
  };

  const formatMaterialQuantityLabel = (text) =>
    String(text ?? "")
      .trim()
      .replace(/,\s*$/, "")
      .replace(/\s+MT$/i, "");

  const formatMaterialsViewValue = (raw) => {
    if (raw == null || raw === "") return "-";

    if (Array.isArray(raw)) {
      const cleaned = raw.map(formatMaterialQuantityLabel).filter(Boolean);
      return cleaned.length ? cleaned.join(", ") : "-";
    }

    const text = String(raw).trim();
    if (!text) return "-";

    if (text.startsWith("[") && text.endsWith("]")) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return formatMaterialsViewValue(parsed);
        }
      } catch {
        /* fall through */
      }
    }

    if (text.includes(",")) {
      const cleaned = text
        .split(/,\s*/)
        .map(formatMaterialQuantityLabel)
        .filter(Boolean);
      return cleaned.length ? cleaned.join(", ") : "-";
    }

    return formatMaterialQuantityLabel(text);
  };

  const getDisplayValue = (input) => {
    const value = input.fieldName.includes(".")
      ? getNestedValue(filteredData, input.fieldName)
      : filteredData?.[input.fieldName];

    if (input.fieldName === "documents") return null;

    if (input.fieldName === "soldProductDate") {
      return formatDate(value || filteredData?.createdAtOriginal);
    }
    if (input.fieldName === "quantitySold") {
      if (value && typeof value === "object") {
        return value.value ? `${value.value} ${value.unit || "MT"}` : "-";
      }
      return value || "-";
    }
    if (input.fieldName === "electricityConsumption") {
      const elec =
        value ??
        filteredData?.energyConsumptionForProcessing ??
        filteredData?.energyDisplay;
      if (elec === 0 || elec === "0") return `0 KWH`;
      return elec ? `${elec} KWH` : "-";
    }
    if (input.fieldName === "fuelDisplay") {
      return formatFuelDisplay(filteredData);
    }
    if (input.fieldName === "processingMethodUsed") {
      const entries = Array.isArray(filteredData?.processingMethodEntries)
        ? filteredData.processingMethodEntries
        : [];
      if (entries.length > 0) {
        return entries
          .map((entry) => {
            const name = entry?.methodName || entry?.processingMethod || "";
            const qty = entry?.quantity;
            if (name && qty !== undefined && qty !== null && String(qty).trim() !== "") {
              return `${name} ${qty}`.trim();
            }
            return name;
          })
          .filter(Boolean)
          .join(", ") || "-";
      }
      const v = filteredData?.processingMethodUsed;
      if (Array.isArray(v)) {
        const joined = v.map((x) => String(x ?? "").trim()).filter(Boolean).join(", ");
        return joined || "-";
      }
      if (typeof v === "string" && v.trim()) return v.trim();
      return "-";
    }
    if (input.fieldName === "materialsAddedDuringProcessing") {
      const entries = Array.isArray(filteredData?.processingMethodEntries)
        ? filteredData.processingMethodEntries
        : [];
      if (entries.length > 0) {
        return entries
          .map((entry) => {
            const name = entry?.methodName || entry?.processingMethod || "";
            const qty = entry?.quantity;
            if (name && qty !== undefined && qty !== null && String(qty).trim() !== "") {
              return `${name} ${qty}`.trim();
            }
            return name;
          })
          .filter(Boolean)
          .join(", ") || "-";
      }

      return formatMaterialsViewValue(value);
    }

    const primitive = value?.value ?? value;
    if (primitive === 0 || primitive === "0") return primitive;
    return primitive ?? "-";
  };

  const list = () => (
    <Box
      display="flex"
      gap={3}
      sx={{
        width: "26.5rem",
        maxWidth: "100%",
        height: "100vh",
        justifyContent: "space-between",
        backgroundColor: "transparent",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "3%",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton
          onClick={() => {
            setViewData(false);
            setData({});
          }}
          sx={{ color: "black" }}
        >
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box sx={{ p: 3, width: "100%", backgroundColor: "#fff", overflow: "auto" }}>
        <Typography
          variant="h6"
          sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
        >
          {title || "Processing of Sold Products Details"}
        </Typography>
        <Grid
          spacing={2}
          sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}
        >
          {inputData?.map((input) => {
            const displayValue = getDisplayValue(input);
            if (displayValue === null) return null;
            if (displayValue === undefined || displayValue === "") return null;

            return (
              <Grid item xs={12} sm={12} key={input.fieldName} mb={1}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", wordBreak: "break-word" }}
                >
                  {input.title}
                </Typography>
                <div style={{ wordBreak: "break-word" }}>{displayValue}</div>
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Documents (optional)
          </Typography>
          {element?.documents?.length > 0 ? (
            <Box display="flex" flexDirection="column" gap={2} sx={{ width: "100%" }}>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {element.documents
                  .filter((url) =>
                    ["png", "jpg", "jpeg"].includes(url.split(".").pop().toLowerCase())
                  )
                  .map((url, index) => (
                    <DocumentItem key={`image-${index}`} url={url} />
                  ))}
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {element.documents
                  .filter(
                    (url) =>
                      !["png", "jpg", "jpeg"].includes(url.split(".").pop().toLowerCase())
                  )
                  .map((url, index) => (
                    <DocumentItem key={`file-${index}`} url={url} />
                  ))}
              </Box>
            </Box>
          ) : (
            <Typography>-</Typography>
          )}
        </Box>
        {!companyAdmin && (
          <Button
            variant="contained"
            sx={{ mt: 2, backgroundColor: "#0a3f73", textTransform: "none" }}
            onClick={() => handleEdit(element)}
          >
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={viewData}
      onClose={() => {
        setViewData(false);
        setData({});
      }}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      {list()}
    </Drawer>
  );
}

export default ViewData;
